import { useEffect } from 'react'

/**
 * Hook to handle anchor scroll positioning on page load and navigation
 * Solves the issue where direct access to anchors (e.g., /page#anchor)
 * doesn't scroll to the correct position compared to link navigation
 */
export const useAnchorScrollFix = () => {
  useEffect(() => {
    const SCROLL_OFFSET = -100 // Same as gatsby-plugin-anchor-links

    const scrollToElement = (element: Element) => {
      const elementPosition =
        element.getBoundingClientRect().top + window.pageYOffset
      const offsetPosition = elementPosition + SCROLL_OFFSET

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }

    const handleAnchorScroll = () => {
      const hash = window.location.hash
      if (!hash) return

      const targetElement = document.querySelector(hash)
      if (!targetElement) return

      // Check if images in the target section are loaded
      const images = targetElement.querySelectorAll('img')
      const imagesToLoad = Array.from(images).filter((img) => !img.complete)

      if (imagesToLoad.length === 0) {
        // All images loaded, scroll immediately
        scrollToElement(targetElement)
      } else {
        // Wait for images to load
        let loadedCount = 0
        const totalImages = imagesToLoad.length

        const onImageLoad = () => {
          loadedCount++
          if (loadedCount === totalImages) {
            scrollToElement(targetElement)
          }
        }

        imagesToLoad.forEach((img) => {
          if (img.complete) {
            onImageLoad()
          } else {
            img.addEventListener('load', onImageLoad, { once: true })
            img.addEventListener('error', onImageLoad, { once: true })
          }
        })

        // Fallback: scroll after a reasonable delay even if images don't load
        setTimeout(() => {
          scrollToElement(targetElement)
        }, 1500)
      }
    }

    const handleInitialLoad = () => {
      if (!window.location.hash) return

      // Use multiple strategies to ensure content is ready
      const strategies = [
        // Strategy 1: Immediate attempt
        () => handleAnchorScroll(),

        // Strategy 2: After DOM is fully ready
        () => {
          if (document.readyState === 'complete') {
            handleAnchorScroll()
          } else {
            window.addEventListener('load', handleAnchorScroll, { once: true })
          }
        },

        // Strategy 3: After React hydration (for SSR)
        () => setTimeout(handleAnchorScroll, 100),

        // Strategy 4: Final fallback
        () => setTimeout(handleAnchorScroll, 500)
      ]

      strategies.forEach((strategy, index) => {
        setTimeout(strategy, index * 50)
      })
    }

    // Handle initial page load with hash
    handleInitialLoad()

    // Handle hash changes (navigation between anchors)
    window.addEventListener('hashchange', handleAnchorScroll)

    return () => {
      window.removeEventListener('hashchange', handleAnchorScroll)
    }
  }, [])
}
